import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { ContentContext } from '@context/ContentContext';
import { useRemediation } from '@hooks/useRemediation';
import CircularLoader from '@common/CircularLoader';
import { Tooltip } from 'antd';
import { VisualisationModal, NotesModal, GeoGebraModal, GeogebraTaxonomyModal } from './QuizModals';
import QuizSelector from './QuizSelector';
import QuizHeader from './QuizHeader';
import QuestionCard from './QuestionCard';
import ExplanationPanel from './ExplanationPanel';
import ProgressStats from './ProgressStats';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FunctionsIcon from '@mui/icons-material/Functions';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import questionsTheme from './questionsTheme';

import useQuizNavigation from './hooks/useQuizNavigation';
import useQuestionAnswering from './hooks/useQuestionAnswering';
import useUserAnnotations from './hooks/useUserAnnotations';
import useAiExplanations from './hooks/useAiExplanations';

const TooltipButton = ({ title, onClick, disabled, children, style }) => (
    <Tooltip title={title}>
        <span style={{ display: 'inline-block', cursor: disabled ? 'not-allowed' : 'default' }}>
            <button
                onClick={onClick}
                disabled={disabled}
                className="ch-button ch-button-icon-l"
                style={{
                    color: disabled ? '#999' : questionsTheme.primaryTeal,
                    border: `1px solid ${disabled ? '#ccc' : questionsTheme.primaryTeal}`,
                    backgroundColor: 'transparent',
                    padding: '4px 8px',
                    pointerEvents: disabled ? 'none' : 'auto',
                    ...style
                }}
            >
                {children}
            </button>
        </span>
    </Tooltip>
);

const Questions = ({ callback }) => {
    const { content } = useContext(ContentContext);
    const conceptId = content?.currentConcept?.conceptId;

    const callbackRef = useRef(callback);
    useEffect(() => { callbackRef.current = callback; });
    useEffect(() => { callbackRef.current?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 1. Core Navigation & Data (Backend-Driven)
    const navState = useQuizNavigation(conceptId);

    // Convert backend dictionary to an ordered array for components that expect lists
    const resultsArray = Object.values(navState.resultsMap || {});

    // 2. Answering Mechanics
    const answerState = useQuestionAnswering(
        conceptId,
        navState.selectedLevel,
        navState.currentQuestion?.questionId,
        resultsArray // passing the mapped array to maintain accurate stats
    );

    // 3. Independent Features
    const annotationState = useUserAnnotations();
    const aiState = useAiExplanations(conceptId);

    // 4. Visualisation
    const [isVisualisationOpen, setIsVisualisationOpen] = useState(false);
    const { remediationWidget, isGenerating, error: remediationError, triggerRemediation, clearRemediation } = useRemediation();

    // 5. GeoGebra Modal
    const [isGeoGebraOpen, setIsGeoGebraOpen] = useState(false);
    const [geoGebraAppletId, setGeoGebraAppletId] = useState(null);

    // 6. GeoGebra Taxonomy Browser
    const [isGeogebraTaxonomyOpen, setIsGeogebraTaxonomyOpen] = useState(false);

    // -- Coordinate State Resets & Intercepts --
    const resetQuizState = () => {
        navState.resetNavigation();
        answerState.resetAnswers();
        aiState.resetExplanations?.();
    };

    const handleStartQuiz = () => {
        resetQuizState();
        navState.handleStartQuiz();
    };

    const handleSelectLevel = (levelObj) => {
        resetQuizState();
        navState.setSelectedLevel(levelObj);
    };

    const handleSelectQuestion = (qId) => {
        if (navState.currentQuestion?.questionId) {
            answerState.captureCurrentQuestion(navState.currentQuestion.questionId);
        }
        navState.loadQuestion(qId);
    };

    const handleCheckAnswer = async (questionId) => {
        const success = await answerState.handleCheckAnswer(questionId);
        if (success) {
            // Update the dots in the header with the fresh backend status
            await navState.reloadResults();
            // Note: We don't auto-navigate here so the user has time to read
            // the 'Correct Answer' feedback and Ai Explanations. They will click 'Next'.
        }
    };

    const handleVisualisationWidget = (currentQuestion) => {
        if (!currentQuestion) return;
        const studentAnswer = answerState.formatUserAnswer(answerState.userAnswers[currentQuestion.questionId], currentQuestion.questionAnswerType);
        triggerRemediation(currentQuestion.questionId, conceptId, studentAnswer, currentQuestion.questionAnswerType);
        setIsVisualisationOpen(true);
    };

    const handleOpenGeoGebra = (currentQuestion) => {
        if (!currentQuestion) return;

        // Backend now populates geogebraAppletId from first GEOGEBRA media in mediaCollection
        if (currentQuestion.geogebraAppletId) {
            setGeoGebraAppletId(currentQuestion.geogebraAppletId);
            setIsGeoGebraOpen(true);
        } else {
            // Fallback: open blank GeoGebra calculator in new tab
            window.open('https://www.geogebra.org/calculator', '_blank');
        }
    };

    const handleSetExplanationIndex = useCallback((qId, idx) => {
        aiState.setCurrentExplanationIndex(prev => ({ ...prev, [qId]: idx }));
        aiState.clearFeedbackStatus(qId);
    }, [aiState]);

    // -- Rendering Gates --
    if (navState.loading || annotationState.favoritesLoading) return <div className="ch-quiz-questions"><CircularLoader displayInElement={true} /></div>;
    if (navState.error) return <div className="ch-quiz-questions ch-error-text margin-y--l" style={{ color: questionsTheme.incorrectRed }}>{navState.error}</div>;
    if (!navState.quizData || !navState.quizData.lessonHasQuestions) return <div className="ch-quiz-questions ch-warning-text margin-y--l">No quizzes available for this lesson.</div>;

    const currentQuestion = navState.currentQuestion;

    // Calculate Frontier: First unattempted question. If all are attempted, frontier is the very last question.
    let frontierIndex = resultsArray.findIndex(q => q.result === undefined || q.result === null);
    if (frontierIndex === -1) {
        frontierIndex = Math.max(0, resultsArray.length - 1);
    }

    const currentQuestionInputs = currentQuestion ? answerState.currentInputs[currentQuestion.questionId] : null;
    const isCheckDisabled = answerState.isSubmitting || !currentQuestion || !!answerState.checkedQuestions[currentQuestion?.questionId] || !currentQuestionInputs || currentQuestionInputs.length === 0;
    const isAnswered = !!answerState.checkedQuestions[currentQuestion?.questionId];

    // Get progress stats
    const progressStats = answerState.getProgressStats();

    // Map AI state for current question
    const explanationsForCard = aiState.explanations?.[currentQuestion?.questionId] || [];
    const isAiVisible = aiState.showExplanation?.[currentQuestion?.questionId];
    const isAiLoading = aiState.loadingExplanation?.[currentQuestion?.questionId];
    const aiIndex = aiState.currentExplanationIndex?.[currentQuestion?.questionId] || 0;
    const aiFeedbackStatus = aiState.feedbackStatus?.[currentQuestion?.questionId];
    const hasExplanationsForQuestion = currentQuestion?.hasExplanations;

    return (
        <div className="ch-quiz-questions ch-width-100">
            {/* Modals */}
            <VisualisationModal
                isOpen={isVisualisationOpen}
                isGenerating={isGenerating}
                remediationError={remediationError}
                remediationWidget={remediationWidget}
                onClose={() => { setIsVisualisationOpen(false); clearRemediation(); }}
            />
            <GeoGebraModal
                isOpen={isGeoGebraOpen}
                appletId={geoGebraAppletId}
                title="GeoGebra Interactive"
                onClose={() => { setIsGeoGebraOpen(false); setGeoGebraAppletId(null); }}
                height={600}
                showToolbar={true}
                showAlgebraInput={true}
            />
            <GeogebraTaxonomyModal
                isOpen={isGeogebraTaxonomyOpen}
                currentQuestion={currentQuestion}
                conceptId={conceptId}
                onClose={() => setIsGeogebraTaxonomyOpen(false)}
            />
            <NotesModal
                isOpen={annotationState.showNotesModal}
                questionList={resultsArray} // using backend roadmap
                notesQuestionId={annotationState.notesQuestionId}
                notes={annotationState.notes}
                currentNoteText={annotationState.currentNoteText}
                setCurrentNoteText={annotationState.setCurrentNoteText}
                onClose={() => { annotationState.setShowNotesModal(false); annotationState.setNotesQuestionId(null); }}
                onSave={annotationState.handleSaveNote}
                onDelete={annotationState.handleDeleteNote}
            />

            {/* Level Tabs + Splash Screen */}
            <QuizSelector
                levelsData={navState.quizData.levelsData}
                conceptName={navState.quizData.conceptName}
                selectedLevel={navState.selectedLevel}
                showQuestions={navState.showQuestions}
                onSelectLevel={handleSelectLevel}
                onStartQuiz={handleStartQuiz}
            />

            {/* Quiz View */}
            {navState.showQuestions && (
                <div>
                    {navState.loadingQuestions && !currentQuestion ? (
                        <CircularLoader displayInElement={true} coreOnly={true}/>
                    ) : (
                        <>
                            <QuizHeader
                                resultsMap={navState.resultsMap}
                                currentQuestion={currentQuestion}
                                favorites={annotationState.favorites}
                                frontierIndex={frontierIndex}
                                onSelectQuestion={handleSelectQuestion}
                            />

                            <ProgressStats stats={progressStats} />

                            {currentQuestion && (
                                <>
                                    {/* Visualisation toolbar */}
                                    <div className="flexRow smallGap margin-b--m">
                                        <TooltipButton
                                            title={isAnswered ? 'Open interactive visualisation for this question' : 'Check your answer to unlock'}
                                            onClick={() => handleVisualisationWidget(currentQuestion)}
                                            disabled={!isAnswered}
                                        >
                                            <AutoGraphIcon className="ch-icon-sm" /> Visualise
                                        </TooltipButton>

                                        <TooltipButton
                                            title={
                                                !isAnswered
                                                    ? 'Check your answer to unlock'
                                                    : currentQuestion.geogebraAppletId
                                                        ? 'Open interactive GeoGebra applet'
                                                        : 'Open GeoGebra calculator in a new tab'
                                            }
                                            onClick={() => handleOpenGeoGebra(currentQuestion)}
                                            disabled={!isAnswered}
                                        >
                                            {currentQuestion.geogebraAppletId ? (
                                                <><FunctionsIcon className="ch-icon-sm" /> GeoGebra</>
                                            ) : (
                                                <><OpenInNewIcon className="ch-icon-sm" /> GeoGebra</>
                                            )}
                                        </TooltipButton>

                                    </div>

                                    <div className="flexRow mediumGap" style={{ alignItems: 'flex-start', flexWrap: 'wrap', width: '100%' }}>
                                        <div style={{ flex: '1 1 0%', minWidth: '400px', display: 'flex' }}>
                                            <QuestionCard
                                                currentQuestion={currentQuestion}
                                                questionIndex={resultsArray.findIndex(r => r.questionId === currentQuestion.questionId)}
                                                questionList={resultsArray}
                                                selectedQuestionId={currentQuestion.questionId}
                                                frontierIndex={frontierIndex}
                                                favorites={annotationState.favorites}
                                                notes={annotationState.notes}
                                                checkedQuestions={answerState.checkedQuestions}
                                                isCorrect={answerState.isCorrect}
                                                isSubmitting={answerState.isSubmitting}
                                                isLoadingQuestion={navState.loadingQuestions}
                                                isCheckDisabled={isCheckDisabled}
                                                userAnswers={answerState.userAnswers}
                                                questionContentRef={answerState.questionContentRef}
                                                onToggleFavorite={annotationState.handleToggleFavorite}
                                                onOpenNotes={annotationState.handleOpenNotes}
                                                onCheckAnswer={handleCheckAnswer}
                                                onRetry={answerState.handleRetry}
                                                onSelectQuestion={handleSelectQuestion}
                                                onInputChange={answerState.handleInputChange}
                                                onRestoreInputs={answerState.restoreQuestionInputs}
                                                onBackToList={resetQuizState}
                                                formatUserAnswer={answerState.formatUserAnswer}
                                                formatCorrectAnswer={answerState.formatCorrectAnswer}
                                            />
                                        </div>

                                        {hasExplanationsForQuestion && (
                                            <div style={{ flex: '1 1 0%', minWidth: '400px', display: 'flex' }}>
                                                <ExplanationPanel
                                                    questionId={currentQuestion.questionId}
                                                    isAnswered={isAnswered}
                                                    showExplanation={{ [currentQuestion.questionId]: isAiVisible }}
                                                    explanations={{ [currentQuestion.questionId]: explanationsForCard }}
                                                    loadingExplanation={{ [currentQuestion.questionId]: isAiLoading }}
                                                    currentExplanationIndex={{ [currentQuestion.questionId]: aiIndex }}
                                                    feedbackStatus={{ [currentQuestion.questionId]: aiFeedbackStatus }}
                                                    onToggleExplanation={aiState.toggleExplanation}
                                                    onSetExplanationIndex={handleSetExplanationIndex}
                                                    onFeedback={aiState.handleFeedback}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Questions;